import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/utils/helpers';
import FeedbackRating from './FeedbackRating';
import type { Feedback } from '../types';

interface FeedbackTableProps {
  feedbacks: Feedback[];
  currentPage: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
  isLoading: boolean;
  isInitialLoading: boolean;
  error: string;
  canUpdate: boolean;
  canDelete: boolean;
  pendingFeedbackId: number | null;
  onPageChange: (page: number) => void;
  onView: (feedback: Feedback) => void;
  onEdit: (feedback: Feedback) => void;
  onDelete: (feedback: Feedback) => void;
  onRetry: () => void;
}

const safeFormatDate = (value: string | null): string => {
  if (!value || Number.isNaN(new Date(value).getTime())) return '—';
  return formatDate(value);
};

const FeedbackTable: React.FC<FeedbackTableProps> = ({
  feedbacks,
  currentPage,
  totalPages,
  totalElements,
  pageSize,
  isLoading,
  isInitialLoading,
  error,
  canUpdate,
  canDelete,
  pendingFeedbackId,
  onPageChange,
  onView,
  onEdit,
  onDelete,
  onRetry,
}) => {
  const { t } = useTranslation();
  const firstItem = totalElements === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const lastItem = Math.min(currentPage * pageSize, totalElements);

  return (
    <div className="relative flex min-h-[420px] flex-1 flex-col">
      {isLoading && !isInitialLoading && (
        <div className="absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-primary-fixed" role="status" aria-label={t('admin.feedbacks.loading')}>
          <div className="h-full w-1/2 animate-pulse bg-primary" />
        </div>
      )}

      <div className="flex-1 overflow-x-auto">
        <table className={`w-full min-w-[980px] border-collapse text-left text-body-sm transition-opacity ${isLoading && !isInitialLoading ? 'opacity-60' : ''}`}>
          <thead className="bg-surface-container-low">
            <tr className="border-b border-outline-variant">
              {['customer', 'product', 'rating', 'comment', 'created_at', 'actions'].map((column) => (
                <th key={column} className={`px-5 py-3 font-label-sm text-label-sm text-secondary ${column === 'actions' ? 'text-right' : ''}`}>
                  {t(`admin.feedbacks.table.${column}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/60 bg-white">
            {isInitialLoading && Array.from({ length: 6 }, (_, index) => (
              <tr key={index}>
                <td colSpan={6} className="px-5 py-3">
                  <div className="h-12 animate-pulse rounded-xl bg-surface-container-low" />
                </td>
              </tr>
            ))}

            {!isInitialLoading && error && feedbacks.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-16 text-center">
                  <span className="material-symbols-outlined text-4xl text-error">cloud_off</span>
                  <p className="mt-2 font-medium text-on-surface">{t('admin.feedbacks.error.title')}</p>
                  <p className="mt-1 text-error">{error}</p>
                  <button type="button" className="mt-4 rounded-xl bg-primary px-4 py-2 font-label-md text-on-primary" onClick={onRetry}>
                    {t('admin.feedbacks.actions.retry')}
                  </button>
                </td>
              </tr>
            )}

            {!isInitialLoading && !error && feedbacks.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-16 text-center text-secondary">
                  <span className="material-symbols-outlined text-4xl text-outline">reviews</span>
                  <p className="mt-2 font-medium text-on-surface">{t('admin.feedbacks.empty.title')}</p>
                  <p className="mt-1">{t('admin.feedbacks.empty.description')}</p>
                </td>
              </tr>
            )}

            {!isInitialLoading && feedbacks.map((feedback) => {
              const username = feedback.username?.trim() || t('admin.feedbacks.customer_fallback');
              const initial = username.charAt(0).toUpperCase();
              const isPending = pendingFeedbackId === feedback.id;
              return (
                <tr
                  key={feedback.id}
                  className={`cursor-pointer hover:bg-surface-container-lowest ${isPending ? 'opacity-60' : ''}`}
                  onClick={() => onView(feedback)}
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-fixed font-label-md text-primary">{initial}</span>
                      <div className="min-w-0">
                        <p className="max-w-[190px] truncate font-medium text-on-surface">{username}</p>
                        <p className="text-[12px] text-secondary">#{feedback.id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="inline-flex rounded-full bg-surface-container px-2.5 py-1 font-label-sm text-secondary">
                      {t('admin.feedbacks.product_id', { id: feedback.productId })}
                    </span>
                  </td>
                  <td className="px-5 py-3"><FeedbackRating rating={feedback.rating} compact /></td>
                  <td className="px-5 py-3">
                    <p className="max-w-[310px] overflow-hidden text-on-surface" style={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 }}>
                      {feedback.comment?.trim() || t('admin.feedbacks.comment_fallback')}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-secondary">{safeFormatDate(feedback.createAt)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-secondary hover:bg-surface-container hover:text-primary disabled:opacity-50"
                        disabled={isPending}
                        aria-label={t('admin.feedbacks.actions.view_label', { id: feedback.id })}
                        onClick={(event) => { event.stopPropagation(); onView(feedback); }}
                      >
                        <span className="material-symbols-outlined text-[19px]">visibility</span>
                      </button>
                      {canUpdate && (
                        <button
                          type="button"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-secondary hover:bg-primary-fixed hover:text-primary disabled:opacity-50"
                          disabled={isPending}
                          aria-label={t('admin.feedbacks.actions.edit_label', { id: feedback.id })}
                          onClick={(event) => { event.stopPropagation(); onEdit(feedback); }}
                        >
                          <span className="material-symbols-outlined text-[19px]">edit</span>
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-error hover:bg-error-container/50 disabled:opacity-50"
                          disabled={isPending}
                          aria-label={t('admin.feedbacks.actions.delete_label', { id: feedback.id })}
                          onClick={(event) => { event.stopPropagation(); onDelete(feedback); }}
                        >
                          <span className="material-symbols-outlined text-[19px]">delete</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <footer className="flex flex-col items-center justify-between gap-3 border-t border-outline-variant bg-white px-4 py-3 text-center sm:flex-row sm:text-left">
        <p className="text-body-sm text-secondary">
          {t('admin.feedbacks.pagination.summary', { first: firstItem, last: lastItem, total: totalElements })}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant text-secondary disabled:opacity-40"
            disabled={currentPage <= 1 || isLoading}
            aria-label={t('admin.feedbacks.pagination.previous')}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
          <span className="min-w-24 text-center font-label-md text-on-surface">
            {t('admin.feedbacks.pagination.page', { current: currentPage, total: Math.max(totalPages, 1) })}
          </span>
          <button
            type="button"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-outline-variant text-secondary disabled:opacity-40"
            disabled={currentPage >= totalPages || isLoading}
            aria-label={t('admin.feedbacks.pagination.next')}
            onClick={() => onPageChange(currentPage + 1)}
          >
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      </footer>
    </div>
  );
};

export default FeedbackTable;
